import React from 'react';
import { useNavigate } from 'react-router-dom';
import adminApi from '../../../services/adminApi';
import CampaignPreviewModal from './CampaignPreviewModal';
import { STATUS_LABELS, formatDateTime } from './campaignUtils';
import styles from './AdminCampaigns.module.css';

export function StatusPill({ status }) {
    return (
        <span className={`${styles.statusPill} ${styles[`status_${status}`] || ''}`}>
            {STATUS_LABELS[status] || status}
        </span>
    );
}

const INITIAL_FILTERS = { q: '', status: '', createdBy: '', from: '', to: '', page: 1 };

/** Admin → Campaigns: campaign history, search/filter and row actions. */
export default function AdminCampaigns() {
    const navigate = useNavigate();
    const [filters, setFilters] = React.useState(INITIAL_FILTERS);
    const [search, setSearch] = React.useState('');
    const [data, setData] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState('');
    const [notice, setNotice] = React.useState('');
    const [busyId, setBusyId] = React.useState(null);
    const [previewId, setPreviewId] = React.useState(null);

    const load = React.useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            setData(await adminApi.listCampaigns(filters));
        } catch (err) {
            setError(err.message || 'Campaigns could not be loaded.');
        } finally {
            setLoading(false);
        }
    }, [filters]);

    React.useEffect(() => { load(); }, [load]);

    // Debounce the free-text search.
    React.useEffect(() => {
        const t = setTimeout(() => {
            setFilters((f) => (f.q === search ? f : { ...f, q: search, page: 1 }));
        }, 300);
        return () => clearTimeout(t);
    }, [search]);

    const setFilter = (key, value) => setFilters((f) => ({ ...f, [key]: value, page: 1 }));

    const runAction = async (id, fn, successMessage) => {
        setBusyId(id);
        setError('');
        setNotice('');
        try {
            const result = await fn();
            if (successMessage) setNotice(typeof successMessage === 'function' ? successMessage(result) : successMessage);
            await load();
            return result;
        } catch (err) {
            setError(err.message || 'Action failed.');
            return null;
        } finally {
            setBusyId(null);
        }
    };

    const duplicate = async (id) => {
        const result = await runAction(id, () => adminApi.duplicateCampaign(id));
        if (result?.campaign) navigate(`/admin/campaigns/${result.campaign.id}`);
    };

    const cancel = (c) => {
        if (!window.confirm(`Cancel the scheduled campaign "${c.name}"? It will not be sent.`)) return;
        runAction(c.id, () => adminApi.cancelCampaign(c.id), 'Scheduled campaign cancelled.');
    };

    const sendTest = (c) => runAction(
        c.id,
        () => adminApi.sendCampaignTest(c.id),
        (r) => `Test email sent to ${r.sentTo}`,
    );

    const campaigns = data?.campaigns || [];
    const hasFilters = Boolean(filters.q || filters.status || filters.createdBy || filters.from || filters.to);
    const showEmptyState = !loading && !error && campaigns.length === 0 && !hasFilters;

    return (
        <div className={styles.page}>
            <div className={styles.header}>
                <div>
                    <h1 className={styles.pageTitle}>Email Campaigns</h1>
                    <p className={styles.subtitle}>Optional product &amp; marketing email. Transactional email (verification, password reset, security) is sent separately.</p>
                </div>
                {!showEmptyState && (
                    <button type="button" className={styles.primaryBtn} onClick={() => navigate('/admin/campaigns/new')}>
                        + New Campaign
                    </button>
                )}
            </div>

            {notice && <div className={styles.successBanner} role="status">{notice}</div>}
            {error && <div className={styles.errorBanner} role="alert">{error}</div>}

            {showEmptyState ? (
                <div className={styles.empty}>
                    <h2 className={styles.emptyTitle}>Email Campaigns</h2>
                    <p className={styles.emptyText}>
                        Create targeted emails for product updates, recommendations, collections, and seasonal Aluvo moments.
                    </p>
                    <button type="button" className={styles.primaryBtn} onClick={() => navigate('/admin/campaigns/new')}>
                        Create your first campaign
                    </button>
                </div>
            ) : (
                <>
                    <div className={styles.filters}>
                        <input
                            className={`${styles.input} ${styles.searchInput}`}
                            placeholder="Search campaign name or subject…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            aria-label="Search campaigns"
                        />
                        <select className={styles.select} value={filters.status} onChange={(e) => setFilter('status', e.target.value)} aria-label="Status">
                            <option value="">All statuses</option>
                            {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select>
                        <select className={styles.select} value={filters.createdBy} onChange={(e) => setFilter('createdBy', e.target.value)} aria-label="Creator">
                            <option value="">Any creator</option>
                            <option value="me">Created by me</option>
                            {(data?.creators || []).map((c) => <option key={c.id} value={c.id}>{c.username}</option>)}
                        </select>
                        <input type="date" className={styles.input} value={filters.from} onChange={(e) => setFilter('from', e.target.value)} aria-label="Created from" />
                        <input type="date" className={styles.input} value={filters.to} onChange={(e) => setFilter('to', e.target.value)} aria-label="Created to" />
                    </div>

                    <div className={styles.tableWrap}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>Campaign name</th>
                                    <th>Subject</th>
                                    <th>Audience</th>
                                    <th>Recipients</th>
                                    <th>Status</th>
                                    <th>Created</th>
                                    <th>Scheduled</th>
                                    <th>Sent</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading && !campaigns.length && (
                                    <tr><td colSpan={9} className={styles.loading}>Loading campaigns…</td></tr>
                                )}
                                {!loading && campaigns.length === 0 && (
                                    <tr><td colSpan={9} className={styles.loading}>No campaigns match these filters.</td></tr>
                                )}
                                {campaigns.map((c) => (
                                    <tr key={c.id}>
                                        <td>
                                            <button type="button" className={styles.campaignName} onClick={() => navigate(`/admin/campaigns/${c.id}`)}>
                                                {c.name}
                                            </button>
                                            {c.creator && <div className={styles.muted}>by {c.creator.username}</div>}
                                        </td>
                                        <td>{c.subject}</td>
                                        <td className={styles.muted}>{c.audienceSummary}</td>
                                        <td className={styles.nowrap}>{['DRAFT', 'SCHEDULED', 'CANCELLED'].includes(c.status) ? '—' : c.recipientCount.toLocaleString()}</td>
                                        <td><StatusPill status={c.status} /></td>
                                        <td className={styles.nowrap}>{formatDateTime(c.createdAt)}</td>
                                        <td className={styles.nowrap}>{c.scheduledAt ? formatDateTime(c.scheduledAt, c.scheduledTimezone) : '—'}</td>
                                        <td className={styles.nowrap}>{c.sentAt ? formatDateTime(c.sentAt) : '—'}</td>
                                        <td>
                                            <div className={styles.actions}>
                                                <button type="button" className={styles.ghostBtn} onClick={() => navigate(`/admin/campaigns/${c.id}`)}>
                                                    {c.status === 'DRAFT' ? 'Edit' : 'View'}
                                                </button>
                                                <button type="button" className={styles.ghostBtn} onClick={() => setPreviewId(c.id)}>Preview</button>
                                                {c.status === 'DRAFT' && c.readiness?.complete && (
                                                    <button type="button" className={styles.ghostBtn} disabled={busyId === c.id} onClick={() => sendTest(c)}>
                                                        Send Test
                                                    </button>
                                                )}
                                                {c.status === 'DRAFT' && (
                                                    <button type="button" className={styles.ghostBtn} onClick={() => navigate(`/admin/campaigns/${c.id}#send`)}>
                                                        Schedule / Send
                                                    </button>
                                                )}
                                                <button type="button" className={styles.ghostBtn} disabled={busyId === c.id} onClick={() => duplicate(c.id)}>Duplicate</button>
                                                {c.status === 'SCHEDULED' && (
                                                    <button type="button" className={styles.ghostBtn} disabled={busyId === c.id} onClick={() => cancel(c)}>Cancel</button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {data?.pagination && data.pagination.totalPages > 1 && (
                        <div className={styles.pagination}>
                            <button type="button" className={styles.ghostBtn} disabled={filters.page <= 1} onClick={() => setFilters((f) => ({ ...f, page: f.page - 1 }))}>← Prev</button>
                            <span className={styles.muted}>Page {data.pagination.page} of {data.pagination.totalPages}</span>
                            <button type="button" className={styles.ghostBtn} disabled={filters.page >= data.pagination.totalPages} onClick={() => setFilters((f) => ({ ...f, page: f.page + 1 }))}>Next →</button>
                        </div>
                    )}
                </>
            )}

            {previewId && <CampaignPreviewModal campaignId={previewId} onClose={() => setPreviewId(null)} />}
        </div>
    );
}
